import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { logo, NAV_LINKS, CONTACT, scrollToId } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo3D from "@/components/Logo3D";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  const navItems = [
    { label: t.nav.whatWeDo, href: "#what-we-do", key: "what-we-do" },
    { label: t.nav.pricing, href: "#pricing", key: "pricing" },
    { label: t.nav.process, href: "#process", key: "process" },
    { label: t.nav.contact, href: "#contact", key: "contact" },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-28 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="logo-home-button"
          className="relative flex items-center"
        >
          <span className="pointer-events-none absolute -inset-4 rounded-full bg-primary/25 blur-2xl" aria-hidden />
          <Logo3D imgClass="relative h-20 md:h-24 w-auto" testid="header-logo-3d" />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {navItems.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.key}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <a
            href={CONTACT.phoneHref}
            data-testid="header-phone-link"
            className="hidden xl:flex items-center gap-2 text-sm font-mono-x text-muted-foreground hover:text-primary transition-colors"
          >
            <Phone className="h-3.5 w-3.5" />
            {CONTACT.phone}
          </a>
          <button
            onClick={() => go("#contact")}
            data-testid="header-get-started-button"
            className="hidden sm:inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-accent transition-colors duration-300"
          >
            {t.nav.getStarted}
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-foreground p-2"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {navItems.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-left text-base font-medium text-foreground/90"
              data-testid={`mobile-nav-${l.key}`}
            >
              {l.label}
            </button>
          ))}
          <div className="pt-2">
            <LanguageSwitcher testid="lang-switcher-mobile" />
          </div>
          <a href={CONTACT.phoneHref} className="text-primary font-mono-x text-sm mt-2">{CONTACT.phone} · {t.hero.hours}</a>
        </div>
      )}
    </motion.header>
  );
}
